import type { CatalogMapPoint, CatalogNearMe, NearbyFallback, RelatedGeoArea } from './catalogsNearMe.types';

/**
 * Redux state slice for Auctions Near Me page
 *
 * Uses catalog-management API to fetch catalogs by geographic bounds.
 * Pagination and filtering are handled server-side.
 */
export type AuctionsNearMeSlice = {
    catalogIds: number[];
    catalogsNearMe: CatalogNearMe[];
    currentPage: number;
    error: string | null;
    /**
     * Pathname whose location could not be resolved to a real place, so that page is a soft 404.
     * Holds the path rather than a boolean so the flag cannot outlive the URL that earned it: the
     * route swap reads it against the current path, and a client-side navigation back to a valid
     * page would otherwise render NotFound for the frame before the loader resets it.
     *
     * Distinct from `error`: a bounds fetch that fails for a location we *did* resolve is a
     * transient failure, not an invalid URL, and must keep returning 200.
     */
    invalidLocationPath: string | null;
    /**
     * The `requestId` of the most recently *dispatched* fetchCatalogsNearMeBy{Bounds,CityAndState}
     * thunk (both share this one field — RTK request ids are globally unique, and both thunks
     * write the same slice fields). `pending` actions fire synchronously in dispatch order, so this
     * always reflects the newest request even when responses resolve out of order. `fulfilled` /
     * `rejected` handlers ignore any action whose `requestId` no longer matches, so a slower,
     * superseded request can never overwrite a newer one's result (or its own `loading` state).
     */
    latestRequestId: string | null;
    loading: boolean;
    mapPoints: CatalogMapPoint[];
    /**
     * Set when this page's results were widened to a radius because the city itself had none, so
     * they belong to neighbouring towns. Null on every page whose results are genuinely its own.
     *
     * Deliberately left alone by the `pending` handlers, exactly like `catalogsNearMe`: the robots
     * meta tag is derived from it, and a value that differs between the server render and the
     * first client render would delete the server-rendered tag (SEAR-1815).
     */
    nearbyFallback: NearbyFallback | null;
    otherPlaces: RelatedGeoArea[];
    totalPages: number;
    totalRecords: number;
    totalUniqueHouses: number;
};

export const defaultAuctionsNearMeSlice: AuctionsNearMeSlice = {
    catalogIds: [],
    catalogsNearMe: [],
    currentPage: 0,
    error: null,
    invalidLocationPath: null,
    latestRequestId: null,
    loading: false,
    mapPoints: [],
    nearbyFallback: null,
    otherPlaces: [],
    totalPages: 0,
    totalRecords: 0,
    totalUniqueHouses: 0,
};
